import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Typography,
  Chip,
  Alert,
  Paper,
  CircularProgress,
  IconButton,
  Switch,
  FormControlLabel,
  Slider,
  Tooltip,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Info as InfoIcon } from '@mui/icons-material';
import {
  createPromptTemplate,
  updatePromptTemplate,
  getAvailableVariables,
  getTemplateUsage,
  createAttributeMapping,
  deleteAttributeMapping,
  snapshotTemplateMappings,
  listBedrockModels,
  BedrockModel,
} from '../services/api';

interface PromptTemplate {
  template_id: string;
  template_name: string;
  template_type: 'WP' | 'CR' | 'SYSTEM';
  content: string;
  description: string;
  variables: string[];
  is_default: boolean;
  status: string;
  model_id?: string | null;
  max_tokens?: number | null;
  temperature?: number | null;
  thinking_enabled?: boolean | null;
  thinking_budget?: number | null;
}

interface MappingRow {
  mapping_id?: string;
  attribute_name: string;
  attribute_value: string;
}

interface PromptTemplateEditorProps {
  open: boolean;
  template: PromptTemplate | null;
  templateType: 'WP' | 'CR' | 'SYSTEM';
  onClose: (refresh: boolean) => void;
}

const PromptTemplateEditor: React.FC<PromptTemplateEditorProps> = ({
  open,
  template,
  templateType,
  onClose,
}) => {
  const [formData, setFormData] = useState({
    template_name: '',
    template_type: templateType,
    description: '',
    content: '',
  });
  const [mappingRows, setMappingRows] = useState<MappingRow[]>([]);
  const [originalMappings, setOriginalMappings] = useState<MappingRow[]>([]);
  const [availableVariables, setAvailableVariables] = useState<string[]>([]);
  const [extractedVariables, setExtractedVariables] = useState<string[]>([]);
  const [invalidVariables, setInvalidVariables] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [previewContent, setPreviewContent] = useState<string>('');
  const contentRef = React.useRef<HTMLTextAreaElement>(null);

  // Per-template Bedrock model config (SCINFRA model-config feature). A
  // single "override" toggle gates the whole fieldset - off means all 5
  // fields inherit from the job/global default, matching what None means on
  // the backend.
  const [modelConfigEnabled, setModelConfigEnabled] = useState(false);
  const [modelId, setModelId] = useState('');
  const [maxTokens, setMaxTokens] = useState<number | ''>('');
  const [temperature, setTemperature] = useState(0.0);
  const [thinkingEnabled, setThinkingEnabled] = useState(false);
  const [thinkingBudget, setThinkingBudget] = useState(1024);
  const [models, setModels] = useState<BedrockModel[]>([]);
  const [loadingModels, setLoadingModels] = useState(false);

  const selectedModel = models.find((m) => m.id === modelId);
  const selectedModelSupportsThinking = !selectedModel || selectedModel.supports_thinking;

  useEffect(() => {
    const fetchModels = async () => {
      try {
        setLoadingModels(true);
        const response = await listBedrockModels();
        if (response.status === 'success') {
          setModels(response.models);
        }
      } catch (err: any) {
        console.error('Error loading Bedrock models:', err);
      } finally {
        setLoadingModels(false);
      }
    };
    fetchModels();
  }, []);

  useEffect(() => {
    if (open) {
      loadAvailableVariables(formData.template_type);
      if (template) {
        // Load template data
        setFormData({
          template_name: template.template_name,
          template_type: template.template_type,
          description: template.description,
          content: template.content,
        });
        // Load existing attribute mappings (a template can have several)
        loadAttributeMappings(template.template_id);

        const hasModelConfig = !!(
          template.model_id ||
          template.max_tokens != null ||
          template.temperature != null ||
          template.thinking_enabled ||
          template.thinking_budget != null
        );
        setModelConfigEnabled(hasModelConfig);
        setModelId(template.model_id || '');
        setMaxTokens(template.max_tokens ?? '');
        setTemperature(template.temperature ?? 0.0);
        setThinkingEnabled(!!template.thinking_enabled);
        setThinkingBudget(template.thinking_budget ?? 1024);
      } else {
        setFormData({
          template_name: '',
          template_type: templateType,
          description: '',
          content: '',
        });
        setMappingRows([]);
        setOriginalMappings([]);
        setModelConfigEnabled(false);
        setModelId('');
        setMaxTokens('');
        setTemperature(0.0);
        setThinkingEnabled(false);
        setThinkingBudget(2000);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, template, templateType]);

  useEffect(() => {
    validateContent();
    updatePreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.content, availableVariables]);

  const loadAvailableVariables = async (type: string) => {
    try {
      const data = await getAvailableVariables(type);
      setAvailableVariables(data.variables || []);
    } catch (err: any) {
      console.error('Failed to load available variables:', err);
      setAvailableVariables([]);
    }
  };

  const loadAttributeMappings = async (templateId: string) => {
    try {
      const data = await getTemplateUsage(templateId);
      const rows: MappingRow[] = (data.mappings || []).map((m: any) => ({
        mapping_id: m.mapping_id,
        attribute_name: m.attribute_name,
        attribute_value: m.attribute_value,
      }));
      setMappingRows(rows);
      setOriginalMappings(rows);
    } catch (err: any) {
      console.error('Failed to load attribute mappings:', err);
      setMappingRows([]);
      setOriginalMappings([]);
    }
  };

  const handleMappingRowChange = (index: number, field: 'attribute_name' | 'attribute_value', value: string) => {
    setMappingRows((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const handleAddMappingRow = () => {
    setMappingRows((prev) => [...prev, { attribute_name: '', attribute_value: '' }]);
  };

  const handleRemoveMappingRow = (index: number) => {
    setMappingRows((prev) => prev.filter((_, i) => i !== index));
  };

  const validateContent = () => {
    const variableRegex = /\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g;
    const matches = [...formData.content.matchAll(variableRegex)];
    const extracted = matches.map((m) => `{${m[1]}}`);
    const uniqueExtracted = Array.from(new Set(extracted));
    setExtractedVariables(uniqueExtracted);

    // Compare variable names without braces (backend returns ["grade", "response"] not ["{grade}", "{response}"])
    const invalid = uniqueExtracted.filter((v) => {
      const varName = v.replace(/[{}]/g, ''); // Strip braces for comparison
      return !availableVariables.includes(varName);
    });
    setInvalidVariables(invalid);
  };

  const updatePreview = () => {
    if (!formData.content) {
      setPreviewContent('');
      return;
    }

    let preview = formData.content;
    const sampleData: Record<string, string> = {
      '{passage}': '[Sample reading passage...]',
      '{prompt}': '[Sample question prompt...]',
      '{response}': '[Sample student response...]',
      '{grade}': '3',
      '{rubric}': '[Rubric text...]',
      '{question}': '[Sample CR question...]',
      '{tot_points}': '4',
    };

    extractedVariables.forEach((v) => {
      const replacement = sampleData[v] || `[${v}]`;
      preview = preview.replace(new RegExp(v.replace(/[{}]/g, '\\$&'), 'g'), replacement);
    });

    setPreviewContent(preview);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleTypeChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      template_type: value as 'WP' | 'CR' | 'SYSTEM',
    }));
    loadAvailableVariables(value);
  };

  const handleVariableClick = (varName: string) => {
    const textarea = contentRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentContent = formData.content;

    // Insert {varName} at cursor position
    const variable = `{${varName}}`;
    const newContent = currentContent.substring(0, start) + variable + currentContent.substring(end);

    // Update content
    setFormData((prev) => ({
      ...prev,
      content: newContent,
    }));

    // Move cursor after inserted variable
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + variable.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const handleSave = async () => {
    setError(null);

    if (!formData.template_name.trim()) {
      setError('Template name is required');
      return;
    }

    if (!formData.content.trim()) {
      setError('Template content is required');
      return;
    }

    if (invalidVariables.length > 0) {
      setError(`Invalid variables: ${invalidVariables.join(', ')}`);
      return;
    }

    // Validate each mapping row: both fields filled, or both empty (empty rows are dropped)
    for (const row of mappingRows) {
      const hasName = row.attribute_name.trim().length > 0;
      const hasValue = row.attribute_value.trim().length > 0;
      if (hasName !== hasValue) {
        setError('Each attribute mapping needs both a name and a value');
        return;
      }
    }

    if (modelConfigEnabled) {
      if (!modelId) {
        setError('Select a model, or turn off the model config override');
        return;
      }
      if (thinkingEnabled && !selectedModelSupportsThinking) {
        setError(`${selectedModel?.name || modelId} does not support extended thinking`);
        return;
      }
      if (thinkingEnabled && maxTokens !== '' && Number(maxTokens) <= thinkingBudget) {
        setError(`Max tokens (${maxTokens}) must exceed thinking budget (${thinkingBudget})`);
        return;
      }
    }

    const validRows = mappingRows.filter(
      (row) => row.attribute_name.trim().length > 0 && row.attribute_value.trim().length > 0
    );

    // Explicit nulls when the override is off, so an existing template's
    // previously-set fields are cleared back to "inherit" - see
    // update_prompt_template's key-presence-vs-null handling on the backend.
    const modelConfigPayload = modelConfigEnabled
      ? {
          model_id: modelId,
          max_tokens: maxTokens === '' ? null : Number(maxTokens),
          temperature,
          thinking_enabled: thinkingEnabled,
          thinking_budget: thinkingEnabled ? thinkingBudget : null,
        }
      : {
          model_id: null,
          max_tokens: null,
          temperature: null,
          thinking_enabled: null,
          thinking_budget: null,
        };

    setLoading(true);
    try {
      let templateId: string;

      if (template) {
        // Update existing template
        await updatePromptTemplate(template.template_id, {
          content: formData.content,
          description: formData.description,
          ...modelConfigPayload,
        });
        templateId = template.template_id;
      } else {
        // Create new template
        const result = await createPromptTemplate({
          template_name: formData.template_name,
          template_type: formData.template_type,
          content: formData.content,
          description: formData.description,
          ...modelConfigPayload,
        });
        templateId = result.template.template_id;
      }

      // Diff mapping rows against what was originally loaded:
      // - rows that changed name/value are recreated (delete old + create new)
      // - rows removed entirely are deleted
      // - new rows (no mapping_id) are created
      const keptMappingIds = new Set(
        validRows
          .filter((row) => row.mapping_id)
          .filter((row) => {
            const original = originalMappings.find((m) => m.mapping_id === row.mapping_id);
            return (
              original &&
              original.attribute_name === row.attribute_name &&
              original.attribute_value === row.attribute_value
            );
          })
          .map((row) => row.mapping_id)
      );

      const mappingsToDelete = originalMappings.filter((m) => m.mapping_id && !keptMappingIds.has(m.mapping_id));
      const mappingsToCreate = validRows.filter((row) => !row.mapping_id || !keptMappingIds.has(row.mapping_id));

      await Promise.all([
        ...mappingsToDelete.map((m) => deleteAttributeMapping(m.mapping_id!)),
        ...mappingsToCreate.map((row) =>
          createAttributeMapping({
            attribute_name: row.attribute_name.trim(),
            attribute_value: row.attribute_value.trim(),
            template_type: formData.template_type,
            template_id: templateId,
          })
        ),
      ]);

      // Record the mapping state as of this save on the version it just produced,
      // so reverting to that version later restores these mappings too.
      await snapshotTemplateMappings(templateId);

      onClose(true);
    } catch (err: any) {
      setError(err.message || 'Failed to save template');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    onClose(false);
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="lg" fullWidth>
      <DialogTitle>{template ? 'Edit Template' : 'Create New Template'}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <TextField
            label="Template Name"
            value={formData.template_name}
            onChange={(e) => handleInputChange('template_name', e.target.value)}
            fullWidth
            required
            disabled={!!template}
            helperText={template ? 'Template name cannot be changed' : 'Alphanumeric and underscores only'}
          />

          <FormControl fullWidth disabled={!!template}>
            <InputLabel>Template Type</InputLabel>
            <Select
              value={formData.template_type}
              onChange={(e) => handleTypeChange(e.target.value)}
              label="Template Type"
            >
              {/* WP Prompts is disabled for now */}
              <MenuItem value="WP" disabled>WP (Writing Prompt)</MenuItem>
              <MenuItem value="CR">CR (Constructed Response)</MenuItem>
              <MenuItem value="SYSTEM">SYSTEM (System Prompt)</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            fullWidth
            multiline
            rows={2}
            helperText="Optional notes about this template"
          />

          <Paper sx={{ p: 2, bgcolor: 'grey.50', border: '1px dashed grey.300' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Model Config (Optional)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Override the Bedrock model/settings used when this template is resolved. Off inherits the
                  job/global default.
                </Typography>
              </Box>
              <Switch
                checked={modelConfigEnabled}
                onChange={(e) => setModelConfigEnabled(e.target.checked)}
              />
            </Box>

            {modelConfigEnabled && (
              <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControl fullWidth disabled={loadingModels}>
                  <InputLabel>Model</InputLabel>
                  <Select
                    value={modelId}
                    onChange={(e) => setModelId(e.target.value)}
                    label="Model"
                  >
                    {loadingModels ? (
                      <MenuItem disabled value="">
                        <CircularProgress size={16} sx={{ mr: 1 }} /> Loading models…
                      </MenuItem>
                    ) : (
                      models.map((m) => (
                        <MenuItem key={m.id} value={m.id}>
                          {m.name}{m.supports_thinking ? ' (supports thinking)' : ''}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>

                <TextField
                  label="Max Tokens"
                  type="number"
                  value={maxTokens}
                  onChange={(e) => setMaxTokens(e.target.value === '' ? '' : Number(e.target.value))}
                  inputProps={{ min: 100, max: 200000 }}
                  helperText="100 – 200,000. Leave blank to inherit."
                  size="small"
                />

                <Box sx={{ px: 1 }}>
                  <Typography gutterBottom variant="body2">
                    Temperature: {temperature.toFixed(1)}
                  </Typography>
                  <Slider
                    value={temperature}
                    onChange={(_, value) => setTemperature(value as number)}
                    min={0}
                    max={1}
                    step={0.1}
                    marks={[
                      { value: 0, label: '0.0' },
                      { value: 0.5, label: '0.5' },
                      { value: 1, label: '1.0' },
                    ]}
                    valueLabelDisplay="auto"
                  />
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={thinkingEnabled}
                        disabled={!selectedModelSupportsThinking}
                        onChange={(e) => setThinkingEnabled(e.target.checked)}
                      />
                    }
                    label="Extended Thinking"
                  />
                  <Tooltip
                    title={
                      selectedModelSupportsThinking
                        ? "Enables Claude's extended thinking (reasoning) mode. Increases token usage."
                        : `${selectedModel?.name || 'This model'} does not support extended thinking`
                    }
                  >
                    <IconButton size="small">
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                {thinkingEnabled && (
                  <TextField
                    label="Thinking Budget (tokens)"
                    type="number"
                    value={thinkingBudget}
                    onChange={(e) => setThinkingBudget(Number(e.target.value))}
                    inputProps={{ min: 100, max: 10000 }}
                    helperText="100 – 10,000 tokens; must be less than Max Tokens"
                    size="small"
                    sx={{ maxWidth: 320 }}
                  />
                )}
              </Box>
            )}
          </Paper>

          <Paper sx={{ p: 2, bgcolor: 'grey.50', border: '1px dashed grey.300' }}>
            <Typography variant="subtitle2" gutterBottom color="text.secondary">
              Attribute Mapping (Optional)
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              Use this template only when a specific attribute condition is met (e.g., isn = 'hisd'). Add multiple
              rows to test this same prompt across several districts/attributes — the template is used if ANY row
              matches the request.
            </Typography>
            {mappingRows.map((row, index) => (
              <Box key={index} sx={{ display: 'flex', gap: 2, mt: 2, alignItems: 'center' }}>
                <TextField
                  label="Attribute Name"
                  value={row.attribute_name}
                  onChange={(e) => handleMappingRowChange(index, 'attribute_name', e.target.value)}
                  placeholder="e.g., isn, district_id"
                  size="small"
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="Attribute Value"
                  value={row.attribute_value}
                  onChange={(e) => handleMappingRowChange(index, 'attribute_value', e.target.value)}
                  placeholder="e.g., hisd, 2700001"
                  size="small"
                  sx={{ flex: 1 }}
                />
                <IconButton size="small" onClick={() => handleRemoveMappingRow(index)} aria-label="Remove mapping">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Button size="small" startIcon={<AddIcon />} onClick={handleAddMappingRow} sx={{ mt: 2 }}>
              Add Mapping
            </Button>
          </Paper>

          <Alert severity="info" sx={{ mb: 1 }}>
            Variables only resolve if they match a field present in the scoring request's JSON body. For fields inside
            nested JSON, use dot notation matching the request structure (e.g. <code>student.grade</code>,{' '}
            <code>metadata.district.isn</code>).
          </Alert>

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Available Variables:
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Click any variable to insert it at your cursor position
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
              {availableVariables.map((v) => (
                <Chip
                  key={v}
                  label={`{${v}}`}
                  size="small"
                  variant="outlined"
                  color="primary"
                  onClick={() => handleVariableClick(v)}
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'primary.light' } }}
                />
              ))}
            </Box>
          </Box>

          <TextField
            label="Template Content"
            value={formData.content}
            onChange={(e) => handleInputChange('content', e.target.value)}
            fullWidth
            required
            multiline
            rows={12}
            helperText="Use {variable_name} for placeholders. Click any variable above to insert."
            inputRef={contentRef}
            sx={{
              '& .MuiInputBase-input': {
                fontFamily: 'monospace',
                fontSize: '0.9rem',
              },
            }}
          />

          {extractedVariables.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Detected Variables:
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {extractedVariables.map((v) => (
                  <Chip
                    key={v}
                    label={v}
                    size="small"
                    color={invalidVariables.includes(v) ? 'error' : 'success'}
                  />
                ))}
              </Box>
              {invalidVariables.length > 0 && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Invalid variables detected: {invalidVariables.join(', ')}
                </Alert>
              )}
            </Box>
          )}

          {previewContent && (
            <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" gutterBottom>
                Preview (with sample data):
              </Typography>
              <Typography
                variant="body2"
                component="pre"
                sx={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                  maxHeight: '300px',
                  overflow: 'auto',
                }}
              >
                {previewContent}
              </Typography>
            </Paper>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={loading || invalidVariables.length > 0}
        >
          {loading ? <CircularProgress size={24} /> : template ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PromptTemplateEditor;
